import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { Waypoint } from 'react-waypoint';
import Button from './Button';
import * as styles from './InfiniteScrollWaypoint.module.scss';
import LoadingIndicator from './LoadingIndicator';

export type FetchMoreHandler = () => Promise<unknown> | void;

interface InfiniteScrollWaypointProps {
  fetchMore?: FetchMoreHandler | null;
  maxConsecutivePage?: number;
}

const InfiniteScrollWaypoint = ({
  fetchMore,
  maxConsecutivePage = 10,
}: InfiniteScrollWaypointProps) => {
  const [state, setState] = useState<
    'idle' | 'loading' | 'error' | 'load-more'
  >('idle');
  const [consecutivePageLoaded, setConsecutivePageLoaded] = useState(0);

  const handleEnter = ({ currentPosition }: Waypoint.CallbackArgs) => {
    if (
      fetchMore &&
      state !== 'loading' &&
      state !== 'error' &&
      currentPosition === 'inside'
    ) {
      const promise = Promise.resolve(fetchMore());
      setConsecutivePageLoaded((p) => p + 1);

      promise
        .catch(async () => {
          await new Promise((resolve) => {
            setTimeout(resolve, 2000);
          });
          return fetchMore();
        })
        .then(
          () => setState('idle'),
          () => setState('error'),
        );

      // We need to set flushSync here, because since automatic batching,
      // it can happen (for example in test code) that the promise either resolves immediately
      // or that the promise resolves in a setTimeout with 0 delay. In both these cases,
      // the call setIsLoading(true) bellow will be batched with setIsLoading(false)
      // above in the finally call. However, the components logic expects that `isLoading`
      // is true at least once when the promise is resolved, because it will cause Waypoint
      // to be unmounted and then remounted again when `isLoading` is false again.
      // Since this behaviour is specific to this component, instead of changing all test code
      // to use setTimeout with delay 1 everywhere, we decided to use flushSync here.
      flushSync(() => {
        setState('loading');
      });
    }
  };

  if (!fetchMore) {
    return null;
  }

  if (state === 'idle' && consecutivePageLoaded < maxConsecutivePage) {
    return <Waypoint onEnter={handleEnter} />;
  }

  return (
    <div className={styles.loader}>
      {state === 'loading' && <LoadingIndicator delay={0} />}
      {state === 'load-more' && (
        <>
          <Waypoint onEnter={handleEnter} />
          <LoadingIndicator delay={0} />
        </>
      )}
      {(state === 'error' || state === 'idle') && (
        <Button
          block
          variant="link"
          onClick={() => {
            setConsecutivePageLoaded(0);
            setState('load-more');
          }}
        >
          {state === 'error' ? 'Try again' : 'Load more'}
        </Button>
      )}
    </div>
  );
};

export default InfiniteScrollWaypoint;
